import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import useTrans from '@hooks/useTrans'
import { ConfigurazioneType, EditorConfigurazioniTreeType, EditorStatusType } from '@utils/types'
import { cn } from '@utils/safeTs'
import { Alert, Button, FormField, FormSelect, Modal } from '@components/atoms'
import styles from './EditorTecnicaEditModal.module.sass'


interface Props {
  open?: boolean
  onClose?: () => void
  status?: EditorStatusType
  onApply?: (v: number) => void 
  configurazioni: EditorConfigurazioniTreeType
  className?: string
}

const EditorTecnicaEditModal = (props: Props) => {

  const { 
    open = false,
    onClose,
    status,
    onApply,
    configurazioni,
    className = '',
  } = props

  const t = useTrans()
  const [localValue, setLocalValue] = useState(status.tecnica?.id?.toString())
  const [errors, setErrors] = useState<{ key: string, level: 'warning' | 'danger', blocking: boolean, message: string }[]>([])

  const tecniche = configurazioni?.tecniche || []
  const tecnicaSelezionata = useMemo(() => {
    return tecniche?.find(t => t.id?.toString() === localValue)
  }, [tecniche, localValue])
  const posizioniDisponibili = useMemo(() => {
    return tecnicaSelezionata?.posizioni || []
  }, [configurazioni, tecnicaSelezionata])

  // TODO - fare le casistiche sui tipi di errori che possono essere sollevati
  const checkErrors = () => {
    let tmpErrors = []

    // check se la nuova tecnica selezionata supporta tutte le posizioni selezionate nello status
    const posizioniDisponibiliIds = posizioniDisponibili?.map(pd => pd.id) || []
    const posizioniNonPresenti = status.posizioni?.filter(p => !posizioniDisponibiliIds.includes(p.configurazione.posizione.id)) || []
    if (posizioniNonPresenti.length > 0)
      tmpErrors.push({
        key: 'missing_posizione',
        level: 'danger',
        blocking: false,
        message: posizioniNonPresenti.length === 1 
          ? t("La posizione <strong>{0}</strong> non è disponibile per questa tecnica e <strong>verrà eliminata</strong>", [posizioniNonPresenti[0]?.configurazione?.posizione?.nome])
          : t("Le posizioni <strong>{0}</strong> non sono disponibili per questa tecnica e <strong>verranno eliminate</strong>")
      })
    // if (true) // TODO
    //   tmpErrors.push({
    //     level: 'warning',
    //     blocking: false,
    //     message: t("Lorem ipsum")
    // })
    setErrors(tmpErrors)
  }

  useEffect(() => {
    setLocalValue(status.tecnica?.id?.toString())
  }, [status.tecnica?.id])

  useEffect(() => {
    checkErrors()
  }, [localValue])

  return (
    <Modal 
      id={'editortecnicaeditmodal'}
      open={open}
      onClose={onClose}
      title={t("Modifica tecnica")}
      buttons={(
        <>
          <Button variant="secondary" size="large" label={t("Annulla")} onClick={onClose} />
          <Button 
            variant='primary' 
            size="large" 
            label={t("Applica modifica")} 
            onClick={onApply ? () => onApply(parseInt(localValue)) : undefined} 
            disabled={status.tecnica?.id?.toString() === localValue || errors?.filter(e => e.blocking)?.length > 0} 
          />
        </>
      )}
      className={cn(styles.root, className)}
      mainClassName={styles.main}
      headerClassName={styles.head}
      bodyClassName={styles.body}
    >
      <Alert 
        full
        variant='warning' 
        styleVariant='background' 
        placeholder={t("La modifica della tecnica potrebbe comportare la perdita delle configurazioni impostate finora")}  
      />
      <div className={styles.selection}>
        <FormField 
          id={'editortecnicaeditmodal_tecnica'}
          label={t("Tecnica")}
        >
          <FormSelect 
            id={'editortecnicaeditmodal_tecnica'}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            options={tecniche.map(t => ({
              label: t.nome, 
              value: t.id?.toString()
            }))}
          />
        </FormField>
        <div className={styles.positions}>
          <div className={styles.positionsTitle}>{t("Posizioni disponibili")}</div>
          <div className={styles.positionsValues}>
            {posizioniDisponibili?.map((p, i) => (
              <span 
                key={p.id}
                className={cn(styles.positionsValue, status.posizioni?.map(sp => sp.id)?.includes(p.id) ? styles['positionsValue--active'] : '')}>
                  {i > 0 && <>,{' '}</>}{p.nome}
              </span>
            ))}
          </div>
        </div>
      </div>
      {!!errors?.length && (
        <div className={styles.errors}>
          {errors.map(error => (
            <Alert
              key={error.key} 
              full
              variant={error.level} 
              styleVariant='background' 
              placeholder={error.message}  
            />
          ))}
        </div>
      )}
    </Modal>
  )
}

export default EditorTecnicaEditModal
